import React, { useEffect, useRef, useState } from "react";
import * as PrimeDataTable from "primereact/datatable";
import { Column } from "primereact/column";
import {
  Badge,
  DropdownItem,
  DropdownMenu,
  Spinner,
  UncontrolledDropdown,
} from "reactstrap";
import { Ripple } from "primereact/ripple";
import { classNames } from "primereact/utils";
import { Dropdown } from "primereact/dropdown";
import { FilterMatchMode } from "primereact/api";
import BizinBackend from "../../helpers/bizin_helper";
import { useSelector } from "react-redux";
import IconWithTooltip from "../Common/IconWithTooltip";
import ImageWithTooltip from "../Common/ImageWithTooltip";
import TextWithTooltip from "../Common/TextWithTooltip";
import { fnReplaceParamKeyWithFilteredValue } from "../../helpers/functions";
import useGetHeightPage from "../../Hooks/useGetHeightPage";
import ButtonWithDropdown from "../Common/ButtonWithDropdown";
import useGetCsrfToken from "../../Hooks/useGetCsrfToken";

/**
 *
 * @param {*} dataKey
 * @description Chave primária da tabela
 * @param {*} data
 * @description Dados da tabela
 * @param {*} procName
 * @description Nome da procedure
 * @param {*} procParams
 * @description Parâmetros da procedure
 * @param {*} columnsList
 * @description Lista <array object> das Colunas da tabela
 * @param {*} paramsOnFilter
 * @description Parâmetros filtrados
 * @param {*} triggerOnFilter
 * @description Gatilho para acionar o filtro
 * @param {*} setTriggerOnFilter
 * @description Função para setar o gatilho
 * @param {*} inputSearchValue
 * @description Valor do input de pesquisa
 * @param {*} onFilteredDataChange
 * @description Função para setar os dados filtrados
 * @param {*} onRowClick
 * @description Função para acionar ao clicar em uma linha
 * @param {*} onDataLoaded
 * @description Função para acionar ao carregar os dados
 * @param {*} onSortChange
 * @description Função para acionar ao ordenar os dados
 * @param {*} enableDataSharing
 * @description Habilitar compartilhamento de dados
 * @param {*} enableMinHeight
 * @description Habilitar altura mínima da tabela
 * @param {*} callbackTriggers
 * @description Função para acionar ao acionar um gatilho
 * @param {*} enableCheckbox
 * @description Habilitar todos os resultados
 * @param {*} enableTotalRows
 * @description Habilitar checkbox
 * @param {*} maxSelectionPerCheckbox
 * @description Máximo de seleções por checkbox
 * @param {*} callbackCheckboxSelected
 * @description Função para acionar ao acionar um checkbox
 * @param {*} enableRowExpansion
 * @description Habilitar expansão de linha
 * @param {*} rowExpansionTemplate
 * @description Template para expansão de linha
 * @param {*} enableCheckboxInHeader
 * @description Habilitar checkbox no header da tabela
 * @param {*} callbackCellEditComplete
 * @description Função para acionar ao editar uma celula do tipo input
 * @param {*} externalSetRowData
 * @description Ref utilizada para expor o setRowData interno da tabela,
 * permitindo atualização externa dos dados renderizados
 * @param {*} hiddenPaginator
 * @description Ocultar paginator
 * @returns
 */

const TableSimple = ({
  dataKey,
  data = [],
  procName,
  procParams,
  columnsList,
  paramsOnFilter,
  triggerOnFilter,
  setTriggerOnFilter,
  inputSearchValue,
  onFilteredDataChange,
  onRowClick,
  // onDataLoaded,
  // onSortChange,
  enableTotalRows = false,
  enableMinHeight = false,
  enableDataSharing = false,
  callbackTriggers = () => { },
  enableCheckbox = false,
  maxSelectionPerCheckbox = 0,
  callbackCheckboxSelected = () => { },
  enableRowExpansion = false,
  rowExpansionTemplate = null,
  enableCheckboxInHeader = true,
  callbackCellEditComplete = () => { },
  externalSetRowData = null,
  rowClassName,
  hiddenPaginator = false
}) => {
  const csrfToken = useGetCsrfToken();
  const [rowData, setRowData] = React.useState([]);
  if (externalSetRowData) {
    externalSetRowData.current = setRowData;
  }
  const [loading, setLoading] = React.useState(false);
  const { user, isIframePage, isModalPage } = useSelector((state) => ({
    user: state.login.user,
    isIframePage: state.Layout.isIframePage,
    isModalPage: state.Layout.isModalPage,
  }));

  const paddingLeft = 10;

  const [totalizersList, setTotalizersList] = useState([]);

  const [selectedProducts, setSelectedProducts] = useState(null);

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(1);

  //const [expandedRows, setExpandedRows] = useState(null);

  const [expandedRowsInternal, setExpandedRowsInternal] = useState({});

  const isAllExpanded =
    rowData.length > 0 &&
    Object.keys(expandedRowsInternal || {}).length === rowData.length;

  const toggleExpandAll = () => {
    if (isAllExpanded) {
      setExpandedRowsInternal({});
    } else {
      const allExpanded = rowData.reduce((acc, row) => {
        acc[row[dataKey]] = true;
        return acc;
      }, {});
      setExpandedRowsInternal(allExpanded);
    }
  };

  // const [selectedProduct, setSelectedProduct] = useState(null);
  const contextMenuCustomPosition = {
    x: 0,
    y: 0,
  };
  const [showContextMenuCustom, setShowContextMenuCustom] = useState(false);
  const contextMenuCustomRef = useRef(null);

  const handleGetData = async () => {
    setLoading(true);
    setTriggerOnFilter(false);

    if (paramsOnFilter?.length)
      procParams = fnReplaceParamKeyWithFilteredValue(
        paramsOnFilter,
        procParams,
      );

    let replaceAllKeysForNull = procParams.replaceAll(/<#[^#]+#>/g, null);
    // .replaceAll(/\s/g, "");
    procParams = JSON.parse(replaceAllKeysForNull);
    procParams = Object.assign({}, procParams);
    procParams = Object.values(procParams).map((e) =>
      e === "null" ? null : e,
    );

    if (procName && procParams) {
      try {
        const data = await BizinBackend.executeProcedure(
          Object.assign({}, procParams),
          procName,
          csrfToken,
          user?.access_token,
        );

        let d = data?.resultset_1 || data;

        if (d) {
          //setRowData(Array.isArray(d) ? d : []);
          setRowData(
            Array.isArray(d)
              ? d.map((item) => ({
                ...item,
                Quantidade_Anular: "",
              }))
              : [],
          );
          if (data?.resultset_2) setTotalizersList(data?.resultset_2);

          // if (onDataLoaded) {
          //   onDataLoaded(d, data?.resultset_2);
          // }
        } else {
          console.log("Retorno inesperado: ", data);
          console.log("procName: ", procName);
        }
      } catch (error) {
        console.log("Erro ao buscar dados [Tabela] :", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const paginatorLeft = (props) => {
    return (
      <>
        {totalizersList?.length > 0 && (
          <div className="d-flex container-left-paginator align-items-center p-0 m-0 gap-4">
            <div
              style={{ width: 150, height: 50 }}
              className="d-flex font-size-12 text-black bg-light-gray-03 p-3 rounded-start-3 justify-content-center align-items-center"
            >
              <span className="text-primary-dark fw-bold">Totalizadores</span>
            </div>
            <div className="col bg-info">
              <div className="d-flex align-items-center gap-3">
                {totalizersList?.map((item, index) => (
                  <div
                    key={index}
                    className="font-size-12 font-w-400 text-dark-gray-primary"
                  >
                    {Object.values(item)[0]}{" "}
                    <span className="text-primary-dark fw-bold">
                      {Object.values(item)[1]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const paginatorTemplate = {
    layout:
      "RowsPerPageDropdown PrevPageLink FirstPageLink PageLinks LastPageLink NextPageLink",
    PrevPageLink: (options) => {
      if (options.props.totalPages < 2) return;
      return (
        <button
          type="button"
          className={`${options.className} ms-3`}
          onClick={options.onClick}
        >
          <i className="fas fa-chevron-left font-size-12"></i>
        </button>
      );
    },
    NextPageLink: (options) => {
      if (options.props.totalPages < 2) return;
      return (
        <button
          type="button"
          className={options.className}
          onClick={options.onClick}
        >
          <i className="fas fa-chevron-right font-size-12"></i>
        </button>
      );
    },
    FirstPageLink: (options) => {
      if (
        options.props.totalPages <= 5 ||
        options.props.page <= 3 ||
        options.props.totalPages === 6
      )
        return;
      return (
        <button
          type="button"
          className={options.className}
          onClick={options.onClick}
        >
          1
        </button>
      );
    },
    LastPageLink: (options) => {
      if (
        options.props.totalPages <= 5 ||
        options.props.page >= options.props.totalPages - 3 ||
        options.props.totalPages === 6
      )
        return;
      return (
        <button
          type="button"
          className={options.className}
          onClick={options.onClick}
        >
          {options.props.totalPages}
        </button>
      );
    },
    PageLinks: (options) => {
      if (options.props.totalPages < 2) return;
      if (
        (options.view.startPage === options.page &&
          options.view.startPage !== 0) ||
        (options.view.endPage === options.page &&
          options.page + 1 !== options.totalPages)
      ) {
        const className = classNames(
          options.className,
          "rounded-0 m-0 no-hover",
        );
        return <span className={className}> ... </span>;
      }

      return (
        <button
          type="button"
          className={options.className}
          onClick={options.onClick}
        >
          {options.page + 1}
          <Ripple />
        </button>
      );
    },
    RowsPerPageDropdown: (options) => {
      const dropdownOptions = [
        { label: 10, value: 10 },
        { label: 20, value: 20 },
        { label: 30, value: 30 },
        { label: "Todos", value: options.totalRecords },
      ];

      return (
        <div
          style={{
            height: 50,
            width: "auto",
            borderStartEndRadius: 8,
            borderEndEndRadius: 8,
            ...(totalizersList?.length !== 0
              ? {
                borderStartStartRadius: 8,
                borderEndStartRadius: 8,
              }
              : {}),
          }}
          className="p-0 m-0 px-3 d-flex align-items-center gap-2 bg-info justify-content-end align-self-baseline"
        >
          <span className="font-size-14 text-black">Resultados por página</span>
          <Dropdown
            className="mx-2"
            value={options.value}
            options={dropdownOptions}
            onChange={options.onChange}
          />
        </div>
      );
    },
  };

  function genericTrigger(type, transaction_id, params, config) {
    switch (type) {
      case "Modal":
        callbackTriggers({
          type,
          config: config[0],
          transaction_id,
          params,
        });
        break;
      case "Blank":
        if (transaction_id?.includes("http"))
          window.open(
            `${transaction_id}${params ? `?${params && isIframePage ? `${params}&${window?.location.search.replace("?", "")}` : params}` : ""}`,
            "_blank",
          );
        else
          window.open(
            window.location.origin +
            `/${transaction_id}${params ? `?${params && isIframePage ? `${params}&${window?.location.search.replace("?", "")}` : params}` : ""}`,
            "_blank",
          );
        break;
      case "Redirect":
        if (transaction_id?.includes("http"))
          window.open(
            `${transaction_id}${params ? `?${params && isIframePage ? `${params}&${window?.location.search.replace("?", "")}` : params}` : ""}`,
            "_self",
          );
        else
          window.open(
            window.location.origin +
            `/${transaction_id}${params ? `?${params && isIframePage ? `${params}&${window?.location.search.replace("?", "")}` : params}` : ""}`,
            "_self",
          );
        break;
      default:
        console.warn(`Tipo ${type} de trigger desconhecido`);
    }
  }

  const cellBodyTemplate = (rowData, props) => {
    const hasHTMLRegex = /<[^>]+>/;
    let image = rowData[props.field];

    try {
      image = JSON.parse(image);
    } catch (error) {
      //console.warn(`Coluna {${props.field}} não é um array de objetos`);
    }

    let isArrayAndNotEmpty = Array.isArray(image) && image?.length;

    function getComponent(item, idx, isMultiValues = false) {
      switch (item?.tipo) {
        case "image":
          return (
            <ImageWithTooltip
              id={idx}
              image={item?.valor}
              describe={item?.descricao}
              fn={() => {
                eval(item?.acao);
              }}
              className={isMultiValues ? "me-2" : ""}
            />
          );
        case "icone":
          return (
            <IconWithTooltip
              id={idx}
              icon={`${item?.valor}`}
              describe={item?.descricao}
              wrapperClass={item?.classeWrapper || ""}
              fn={() => {
                eval(item?.acao);
              }}
            />
          );
        case "kebab":
          return (
            <ButtonWithDropdown
              idx={idx}
              icone={`${item?.valor}`}
              options={item?.options}
              callbackAction={(action) => eval(action)}
            />
          );
        case "texto":
          if (item?.descricao_tooltip) {
            return (
              <div
                onClick={() => {
                  eval(item?.acao);
                }}
              >
                <TextWithTooltip
                  id={idx}
                  text={item?.valor}
                  textFull={item?.descricao_tooltip}
                />
              </div>
            );
          }
          return (
            <div>
              <span
                onClick={() => {
                  eval(item?.acao);
                }}
              >
                {item?.valor}
              </span>
            </div>
          );
        case "badge":
          return (
            <Badge className={item?.classe} id={idx}>
              {item?.valor}
            </Badge>
          );
        default:
          return (
            <i
              className="far fa-user-circle font-size-32 user-photo-label"
              aria-hidden="true"
            />
          );
      }
    }

    return (
      <div
        className={`d-flex align-items-center gap-1 justify-content-${props?.column?.props?.alignItem || "start"} ${rowData?.Flag_Visualizacao === "N" ? "no-visited" : ""} ${rowData?.Flag === "E" ? "flag-error" : ""} ${rowData?.Flag === "I" ? "flag-success" : ""} ${rowData?.Flag === "A" ? "flag-alert" : ""}`}
      >
        {props?.column?.props?.hasImage ? (
          <>
            {isArrayAndNotEmpty
              ? image?.map((item, idx) =>
                getComponent(
                  item,
                  `${props.rowIndex}-${idx}-${props?.column?.props?.field}`,
                  image?.length > 1,
                ),
              )
              : getComponent(
                image,
                `${props.rowIndex}-${props?.column?.props?.field}`,
              )}
          </>
        ) : (
          <>
            {hasHTMLRegex.test(image) ? (
              <div dangerouslySetInnerHTML={{ __html: image }}></div>
            ) : (
              // <div className={`${props?.column?.props?.isInput ? 'form-control d-flex align-items-center px-2 font-size-14' : ''} ${rowData?.Input_Bloqueio === "S" ? "unclickable" : ""}`} style={{...(props?.column?.props?.isInput ? {height: 34} : {})}}>
              //   <span>{rowData[props.field]}</span>
              // </div>
              <>
                {props?.column?.props?.isInput === true ? (
                  <input
                    type="text"
                    disabled={rowData?.Input_Bloqueio === "S"}
                    className="form-control font-size-14"
                    style={{ height: 34, paddingLeft: 12 }}
                    value={rowData[props.field]}
                  />
                ) : (
                  <span>{rowData[props.field]}</span>
                )}
              </>
            )}
          </>
        )}
      </div>
    );
  };

  const onGlobalFilterChange = (value) => {
    let _filters = { ...filters };
    _filters["global"].value = value;
    setFilters(_filters);
  };

  const customSort = (field) => {
    setSortField(field);
    setSortOrder(sortOrder === 1 ? -1 : 1);
  };

  const sortedData = React.useMemo(() => {
    const sorted = [...rowData]?.sort((a, b) => {
      if (sortField) {
        let compareValueA = a[sortField];
        let compareValueB = b[sortField];

        try {
          let extractValueA = JSON.parse(a[sortField]);
          let extractValueB = JSON.parse(b[sortField]);

          Object.values(extractValueA).find((i) => {
            compareValueA = i.valor;
          });

          Object.values(extractValueB).find((i) => {
            compareValueB = i.valor;
          });
        } catch (error) {
          console.warn("SORT: item da coluna não é um array de objeto ");
        }

        const isDateBR = (v) =>
          typeof v === "string" &&
          /^\d{2}\/\d{2}\/\d{4}( \d{2}:\d{2}:\d{2})?$/.test(v.trim());

        const parseDateBR = (v) => {
          const [date, time = "00:00:00"] = v.split(" ");
          const [d, m, y] = date.split("/");
          const [h, min, s] = time.split(":");

          return new Date(y, m - 1, d, h, min, s).getTime();
        };

        const isMoney = (v) =>
          typeof v === "string" &&
          /^(R\$\s?)?-?\d{1,3}(\.\d{3})*(,\d{2})$/.test(v.trim());

        const parseBRL = (v) =>
          parseFloat(
            v
              .replace(/[^\d,-]/g, "")
              .replace(/\./g, "")
              .replace(",", "."),
          );

        const isNumber = (v) =>
          typeof v === "number" || (!isNaN(v) && v !== null && v !== "");

        const naturalCompare = (a, b) => {
          const tokenize = (str) =>
            String(str)
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .toLowerCase()
              .match(/(\d+|\D+)/g);

          const A = tokenize(a);
          const B = tokenize(b);

          const len = Math.max(A?.length, B?.length);

          for (let i = 0; i < len; i++) {
            const partA = A[i];
            const partB = B[i];

            if (partA === undefined) return -1;
            if (partB === undefined) return 1;

            const numA = Number(partA);
            const numB = Number(partB);

            const isNumA = !isNaN(numA);
            const isNumB = !isNaN(numB);

            if (isNumA && isNumB) {
              if (numA !== numB) return numA - numB;
            } else {
              const result = partA.localeCompare(partB, "pt-BR", {
                sensitivity: "base",
              });

              if (result !== 0) return result;
            }
          }

          return 0;
        };

        if (compareValueA == null) compareValueA = "";
        if (compareValueB == null) compareValueB = "";

        // 🗓️ DATA
        if (isDateBR(compareValueA) && isDateBR(compareValueB)) {
          return (
            (parseDateBR(compareValueA) - parseDateBR(compareValueB)) *
            sortOrder
          );
        }

        // 💰 MOEDA
        if (isMoney(compareValueA) && isMoney(compareValueB)) {
          return (
            (parseBRL(compareValueA) - parseBRL(compareValueB)) * sortOrder
          );
        }

        // 🔢 NÚMERO
        if (isNumber(compareValueA) && isNumber(compareValueB)) {
          return (Number(compareValueA) - Number(compareValueB)) * sortOrder;
        }

        // 📝 TEXTO (natural)
        return naturalCompare(compareValueA, compareValueB) * sortOrder;
      }

      return 0;
    });

    return sorted;
  }, [rowData, sortField, sortOrder]);

  const filteredData = React.useMemo(() => {
    let filtered = [...sortedData];

    if (filters.global.value) {
      filtered = filtered.filter((row) => {
        return Object.values(row).some((value) => {
          if (value === null || value === undefined) return false;
          return String(value)
            .toLowerCase()
            .includes(filters.global.value.toLowerCase());
        });
      });
    }

    return filtered;
  }, [sortedData, filters.global.value]);

  const renderHeader = (col) => {
    return (
      <div className="flex align-items-center justify-content-center">
        {col?.header}
        {col?.sort && (
          <>
            {sortField === col?.field && (
              <i
                className={`fas fa-sort-${sortOrder === 1 ? "down mb-1" : "up mt-1"
                  } ps-2 text-light-blue-11`}
              ></i>
            )}
            {sortField !== col?.field && <i className="ps-2 fas fa-sort"></i>}
          </>
        )}
      </div>
    );
  };

  const inputEditable = (options) => {
    return (
      <input
        type="text"
        className="form-control font-size-14 text-color-cinza5"
        style={{ height: 34 }}
        value={options.value ?? ""}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => options.editorCallback(e.target.value)}
      />
    );
  };

  const onCellEditComplete = (e) => {
    const { rowData, newValue, newRowData, field } = e;

    // if changed value
    if (rowData[field] === newValue) return;

    setRowData((prev) =>
      prev.map((item) =>
        item[dataKey] === newRowData[dataKey]
          ? {
            ...item,
            [field]: newValue,
          }
          : item,
      ),
    );
    callbackCellEditComplete(newRowData);
  };

  useGetHeightPage();

  useEffect(() => {
    if (data && data.length !== 0) {
      setRowData(data);
    }
  }, [data]);

  useEffect(() => {
    if (triggerOnFilter && procName) handleGetData();
  }, [triggerOnFilter]);

  useEffect(() => {
    onGlobalFilterChange(inputSearchValue);
  }, [inputSearchValue]);

  useEffect(() => {
    if (enableDataSharing && onFilteredDataChange) {
      onFilteredDataChange(filteredData);
    }
  }, [filteredData, enableDataSharing, onFilteredDataChange]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        contextMenuCustomRef.current &&
        contextMenuCustomRef.current instanceof HTMLElement
      ) {
        if (!contextMenuCustomRef.current.contains(event.target)) {
          setShowContextMenuCustom(false);
        }
      } else {
        setShowContextMenuCustom(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <div className="w-100">
      <PrimeDataTable.DataTable
        className={`dataTable-custom-primary table-simple-container ${!enableCheckboxInHeader ? "no-header-checkbox" : ""} ${!isModalPage && enableMinHeight && "active-min-height"}`}
        tableClassName="table mb-0 table-light font-size-14 table-sm"
        rowClassName={rowClassName}
        value={sortedData}
        paginatorTemplate={hiddenPaginator ? null :paginatorTemplate}
        sortMode="single"
        //groupRowsBy={dataKey}
        //rowGroupMode="rowspan"
        expandedRows={enableRowExpansion ? expandedRowsInternal : null}
        onRowToggle={
          enableRowExpansion
            ? (e) => {
              setExpandedRowsInternal(e.data);
            }
            : undefined
        }
        rowExpansionTemplate={
          enableRowExpansion ? rowExpansionTemplate : undefined
        }
        // expandedRows={expandedRows}
        // onRowToggle={(e) => setExpandedRows(e.data)}
        dataKey={dataKey}
        loading={loading}
        loadingIcon={<Spinner className="text-primary" size="sm" />}
        reorderableColumns={true}
        paginatorClassName="paginator-table-custom pt-3 pb-3 px-0"
        paginatorLeft={paginatorLeft}
        // footerColumnGroup={resultset2?.isFooter == "S" ? footerGroup : <></>}
        pageLinkSize={6}
        // totalRecords={total}
        paginator
        rows={enableTotalRows ? sortedData.length || 10 : 10}
        //input filter
        emptyMessage={"Nenhum registro encontrado."}
        filters={filters}
        onRowClick={onRowClick}
        selection={enableCheckbox && selectedProducts}
        selectionMode={enableCheckbox && "single"} //checkbox
        onSelectionChange={(e) => {
          if (
            maxSelectionPerCheckbox &&
            e.value.length > maxSelectionPerCheckbox
          )
            return;
          setSelectedProducts(e.value);
          callbackCheckboxSelected(e.value);
          if (e.value?.Linha_Acao?.includes("genericTrigger"))
            eval(e.value?.Linha_Acao);
        }}
        editMode="cell"
      >
        {enableCheckbox && (
          <Column
            selectionMode={enableCheckbox ? "multiple" : null}
            headerStyle={{ padding: 5, background: "#D9D9D9" }}
          ></Column>
        )}

        {enableRowExpansion && (
          <Column
            expander
            header={
              <button
                type="button"
                className="p-0 m-0 d-flex justify-content-center align-items-center"
                style={{
                  background: "transparent",
                  border: "none",
                  boxShadow: "none",
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleExpandAll();
                }}
                title={isAllExpanded ? "Recolher todos" : "Expandir todos"}
              >
                <i
                  className={`fas ${isAllExpanded ? "fa-caret-down" : "fa-caret-right"
                    } header-expander-icon`}
                />
              </button>
            }
            headerStyle={{
              background: "#D9D9D9",
              textAlign: "center",
            }}
            bodyStyle={{ padding: "0 0 0 10px", width: 10 }}
          />
        )}
        {columnsList.map((col, i) => (
          <Column
            style={{ paddingLeft: i === 0 ? paddingLeft : 10 }}
            key={col.field}
            sortable={col?.sort}
            hidden={col?.hidden || false}
            sortFunction={(e) => {
              setSortField(e.field);
              setSortOrder(e.order);

              // if (onSortChange) {
              //   onSortChange(e.field, e.order);
              // }

              return e.data;
            }}
            headerStyle={{
              background: "#D9D9D9",
              width: col?.width ? col.width : "auto",
            }}
            bodyStyle={{
              padding: `0 10px 0 ${paddingLeft}`,
            }}
            className={col?.styles ? col?.styles : ""}
            hasImage={col?.hasImage}
            isInput={col?.input}
            alignItem={col?.align || "start"}
            field={col.field}
            body={cellBodyTemplate}
            editor={
              col?.input ? (options) => inputEditable(options) : undefined
            }
            onCellEditComplete={col?.input ? onCellEditComplete : undefined}
            header={renderHeader(col)}
          />
        ))}
      </PrimeDataTable.DataTable>

      {/* Context menu custom */}
      <UncontrolledDropdown
        isOpen={showContextMenuCustom}
        ref={contextMenuCustomRef}
        toggle={() => setShowContextMenuCustom(!showContextMenuCustom)}
        className="absolute context-menu-custom"
        style={{
          top: contextMenuCustomPosition.y - 890,
          left: contextMenuCustomPosition.x - 80,
        }}
      >
        <DropdownMenu className="dropdown-kebab-custom-table dropdown-menu-end">
          <DropdownItem className="d-flex align-items-center">
            <a href="/" className="p-0 m-0">
              <i className="ri-pencil-fill align-bottom me-2 text-color-azul8" />{" "}
              Action test{" "}
            </a>
          </DropdownItem>
          <DropdownItem className="d-flex align-items-center">
            <a href="/" className="p-0 m-0">
              <i className="ri-pencil-fill align-bottom me-2 text-color-azul8" />{" "}
              Action test{" "}
            </a>
          </DropdownItem>
        </DropdownMenu>
      </UncontrolledDropdown>
    </div>
  );
};

export default TableSimple;
